import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { t, lookups } from '../../content';
import { useLocale } from '../../locale';
import { setCompanyInfo } from '../../store/actions/companyActions'
import Form from '../../components/reusableElementsComponents/Form';
import Button from '../../components/reusableElementsComponents/Button';
import WriteAccessGuard from '../../components/reusableElementsComponents/WriteAccessGuard';
import Loading from '../../components/reusableElementsComponents/Loading';
import Loader from '../../components/reusableElementsComponents/Loader';
import UserList from './usersList';
import DefineRoles from './DefineRoles';
import AssignTeamsComp from './AssignTeamsComp';
import AssignPoolsComp from './AssignPoolsComp';
import CreditsConsumption from './CreditsConsumption';
import phone from '../../assets/images/icons/phone.svg';
import company from '../../assets/images/icons/companyIcon.svg';
import calculator from '../../assets/images/icons/calculator.svg';
import users from "../../assets/images/icons/users.png";
import roles from "../../assets/images/icons/roles.png";
import assignTeamsImg from "../../assets/images/icons/assignTeams.png";
import assignPoolsImg from "../../assets/images/icons/assignPools.png";
import logoPlaceholder from '../../assets/images/logos/logo-placeholder.png';
import * as Utilities from "../../Utilities";
import * as Constants from '../../constants'
import B2BApiService from "../../B2bApiService";
import { ToastContainer, toast } from "react-toastify";

import Col from 'react-bootstrap/Col';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';

function CorporateSettings({ userRole: preFetchedUserRole, userEmail: preFetchedUserEmail, companyId, iamEnabled }) {
  const { locale } = useLocale();
  const [userRole, setUserRole] = useState(preFetchedUserRole);
  const [userEmail, setUserEmail] = useState(preFetchedUserEmail);

  const allowEditCompanyDetails = Constants.ADMINISTRATIVE_USER_ROLES.includes(userRole);
  const creditsConsumptionEnabled = Constants.CREDIT_CONSUMPTION_ENABLED_COMPANIES.includes(companyId);
  // const data = useSelector(state => state.contentfulData.all['settings']);

  // Company data: The company to which the corporate user belongs
  const companyData = useSelector(state => state.company.info);

  const [template, setTemplate] = useState({});
  const [loader, setLoader] = useState(false);

  const hasFetchedRef = useRef(false);

  const dispatch = useDispatch();
  const b2bServiceObj = useMemo(() => {
    return new B2BApiService();
  }, []);

  useEffect(() => {
    if (Utilities.isEmptyObject(companyData) || !companyData.name) {
      b2bServiceObj.LoadCompanyData();
    }
  }, [b2bServiceObj, companyData]);

  useEffect(() => {
    // Fetch current logged in user data again to make sure we have the latest user role and info
    b2bServiceObj.LoadUserData((userData) => {
      localStorage.setItem(Constants.LOCAL_STORAGE_KEY_USER_ROLE_ID, userData.userRole)
      setUserRole(userData.userRole);
      setUserEmail(userData.email);
    })

    // Fetch all corporate users of the company. Only Owner, Admin and HRD can access this API
    if (userRole && [...Constants.ADMINISTRATIVE_USER_ROLES, Constants.USER_ROLE_HRD].includes(userRole)) {
      if (hasFetchedRef.current) return; // Make sure we only fetch once
      hasFetchedRef.current = true;
      b2bServiceObj.GetCompanyAccounts();
    }
  }, [b2bServiceObj, userRole]);

  useEffect(() => {
    if (!Utilities.isEmptyObject(companyData)) {
      setTemplate({
        fields: [
          {
            title: t('common.fields.companyName'),
            type: 'text',
            name: 'companyName',
            placeholder: t('common.placeholders.startTyping'),
            defaultValue: companyData?.name,
            icon: company,
            disabled: !allowEditCompanyDetails
          },
          {
            title: t('common.fields.phoneNumber'),
            type: 'tel',
            name: 'phoneNumber',
            placeholder: t('common.placeholders.startTyping'),
            defaultValue: companyData?.contact_phone,
            icon: phone,
            disabled: !allowEditCompanyDetails
          },
          {
            title: t('common.fields.employeeCount'),
            type: 'text',
            name: 'employeeCount',
            placeholder: t('common.placeholders.selectTeamSize'),
            dropdown: true,
            defaultValue: companyData?.employee_count,
            dropdownTemplate: lookups.employeeSizeOptions,
            disabled: !allowEditCompanyDetails
          }
        ]
      })
    }
  }, [companyData, allowEditCompanyDetails, locale]);

  const onSubmit = async (values) => {
    // Save company details. Only for allowed user roles
    if (!allowEditCompanyDetails) {
      return;
    }

    setLoader(true);
    const savingCompanyDetails = toast.loading(t('account.toasts.updatingCompany'))
    try {
      const saveCompanyDetailsResponse = await b2bServiceObj.SaveCompanyData({
        "company_name": values?.companyName?.length > 0 ? values?.companyName : companyData.name,
        "employee_count": values?.employeeCount?.length > 0 ? values?.employeeCount : companyData.employee_count,
        "contact_phone": values?.phoneNumber?.length > 0 ? values?.phoneNumber : '',
        "logo": companyData.logo ? companyData.logo : '',
      })
      if (saveCompanyDetailsResponse && saveCompanyDetailsResponse.httpStatus === 200) {
        const res = saveCompanyDetailsResponse.json;
        dispatch(setCompanyInfo(
          {
            name: res?.name,
            employee_count: res?.employee_count,
            contact_phone: res?.contact_phone,
            industry: res?.industry,
            logo: res?.logo,
          }));
        toast.update(savingCompanyDetails, { render: t('account.toasts.companyUpdated'), type: "success", isLoading: false, autoClose: 2000, closeButton: true, hideProgressBar: true });
      } else {
        toast.update(savingCompanyDetails, { render: t('account.toasts.companyUpdateError'), type: "error", isLoading: false, autoClose: 2000, closeButton: true, hideProgressBar: true });
      }
    } catch (error) {
      toast.update(savingCompanyDetails, { render: t('account.toasts.companyUpdateError'), type: "error", isLoading: false, autoClose: 2000, closeButton: true, hideProgressBar: true });
    }

    setLoader(false);
    // window.scrollTo(0, 0);
  };

  return (
    <div className='page-layout-wrapper'>
      <ToastContainer autoClose={1000} containerId="IAMToast" />
      <div className='inner-layout'>
        <Tab.Container id="corporate-settings-tabs" defaultActiveKey="first">
          <Row className='w-100'>
            <Col sm={12} className='d-flex vp-1200'>
              <div>
                <Nav variant="pills" className="flex-column bg-white py-3 px-2 mb-3 rounded" style={{width: "300px"}}>
                  <strong className='m-2'>
                    {t('iam.corporateSettings')}
                  </strong>
                  <hr style={{ margin: '0px 0px 10px 0px' }} />
                  <Nav.Item>
                    <Nav.Link eventKey="first"> <img className='m-2' src={company} alt='company info'/> {t('iam.companyInfo')} </Nav.Link>
                  </Nav.Item>
                  {creditsConsumptionEnabled &&
                    <Nav.Item>
                      <Nav.Link eventKey="credits"> <img className='m-2' src={calculator} alt='credits consumption'/> {t('iam.creditsConsumption')} </Nav.Link>
                    </Nav.Item>
                  }
                </Nav>
                {iamEnabled &&
                  <Nav variant="pills" className="flex-column bg-white py-3 px-2 mb-3 rounded" style={{width: "300px"}}>
                    <strong className='m-2'>
                      {t('iam.identityAndAccess')}
                    </strong>
                    <hr style={{ margin: '0px 0px 10px 0px' }} />
                    <Nav.Item>
                      <Nav.Link eventKey="second"> <img height="20px" className='m-2' src={users} alt='users'/> {t('iam.allCorporateUsers')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="third"> <img height="20px" className='m-2' src={roles} alt='roles'/> {t('iam.rolesAndPermissions')}</Nav.Link>
                    </Nav.Item>
                    {![Constants.USER_ROLE_OWNER].includes(userRole) && <>
                      <Nav.Item>
                        <Nav.Link eventKey="fourth"> <img height="20px" className='m-2' src={assignTeamsImg} alt='assign-teams'/> {t('iam.assignTeams')}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="fifth"> <img height="20px" className='m-2' src={assignPoolsImg} alt='assign-pools'/> {t('iam.assignPools')}</Nav.Link>
                      </Nav.Item>
                    </>}
                  </Nav>
                }
              </div>
              <Tab.Content className='w-100'>
                <Tab.Pane eventKey="first">
                  {companyData?.name ? (
                    <div className="settings-page">
                      {!Utilities.isEmptyObject(template) &&
                      <div className="settings-page-wrapper">
                        <div className="settings">
                          <ToastContainer />
                          <img src={companyData?.logo?.length > 0 ? Constants.IMAGES_BASE_URL + companyData.logo : logoPlaceholder}
                            alt="company logo" id="companyLogo"
                            style={{
                              width: 'auto',
                              height: '100px',
                              margin: '10px'
                            }}
                          />
                          <Form
                            key={locale}
                            template={template}
                            onSubmit={onSubmit}
                          >
                            {loader ?
                              <Button btnstyle={'mazhrBtn light-grey'}>
                                <Loader />
                              </Button>
                              :
                              <WriteAccessGuard>
                                <Button type='submit' btnstyle={'mazhrBtn default-green white'} onSubmit={onSubmit}>
                                {t('common.actions.save')}
                              </Button>
                              </WriteAccessGuard>
                            }
                          </Form>
                        </div>
                      </div>}
                    </div>)
                    :
                    <Loading />
                  }
                </Tab.Pane>
                {creditsConsumptionEnabled &&
                  <Tab.Pane eventKey="credits"><CreditsConsumption companyId={companyId} /></Tab.Pane>
                }
                {iamEnabled && <>
                  <Tab.Pane eventKey="second"><UserList userRole={userRole} userEmail={userEmail} /></Tab.Pane>
                  <Tab.Pane eventKey="third"><DefineRoles/></Tab.Pane>
                  {/* Owner and Admin should not interfere with Operational features */}
                  {![Constants.USER_ROLE_OWNER].includes(userRole) && <>
                    <Tab.Pane eventKey="fourth"><AssignTeamsComp/></Tab.Pane>
                    <Tab.Pane eventKey="fifth"><AssignPoolsComp/></Tab.Pane>
                  </>}
                </>}
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </div>
    </div>
  )
}

export default CorporateSettings
